import React, { Component } from "react";
import { trackCartRemoval } from "../../Helpers/marketingEvents";
import { cartChangeNoticeKey, visibleCartChanges } from "../domain/cartChangeNotices";
import { getCartValidationNotice } from "../domain/cartIssues";
import { getCartReasonLabel } from "../domain/cartChangeReasons";
import { getCartLineDetails, getCartProductPresentation } from "../domain/cartPresentation";
import CircularProgress from "@material-ui/core/CircularProgress";
import Drawer from "@material-ui/core/Drawer";
import Snackbar from "@material-ui/core/Snackbar";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import DeleteOutlineRoundedIcon from "@material-ui/icons/DeleteOutlineRounded";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import RemoveRoundedIcon from "@material-ui/icons/RemoveRounded";
import ShoppingBasketOutlinedIcon from "@material-ui/icons/ShoppingBasketOutlined";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import ArrowBackRoundedIcon from "@material-ui/icons/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@material-ui/icons/ArrowForwardRounded";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { getMaximumProductInventoryFromModifiers } from "../../Components/Menu/Helpers";
import { resolveAreaName } from "../../Components/Storefront/F0/contextSummary";
import { fetchDeliveryTime } from "../../store/actions/deliveryTime";
import {
  add_promo_item,
  add_to_cart,
  edit_quantity,
  remove_from_cart,
  setProduct,
} from "../../store/actions";
import { getProduct, getProductInventory } from "../../store/reducers/store";
import { checkoutV2Api } from "../api";
import {
  buildCheckoutLines,
  classifyCheckoutLines,
  createCartFingerprint,
  getEffectiveCheckoutLines,
  getCheckoutLineId,
} from "../domain/cartContext";
import {
  createFulfillmentFingerprint,
  getFulfillmentIssues,
  normalizeFulfillmentContext,
} from "../domain/fulfillmentContext";
import { moneyToMajor, normalizeMoneyValue } from "../domain/money";
import { formatCheckoutTimeSummary } from "../domain/timeSummary";
import "../styles/typography.css";
import {
  clearCheckoutV2Quote,
  dismissCartChanges,
  setCheckoutV2CartValidation,
  setCheckoutV2Quote,
} from "../state/actions";
import {
  recordStorefrontV2Telemetry,
  STOREFRONT_V2_TELEMETRY,
} from "../telemetry";
import Money from "./Money";
import CartPromotionCode from "./CartPromotionCode";
import DeferredFeedback from "./DeferredFeedback";
import "./CartDrawerV2.css";
import StorefrontActionButton from "../../Components/Storefront/ActionButton/StorefrontActionButton";
import CartDrawerHistory from "../routes/cartDrawerHistory";
import { getQuoteDisplayRounding } from "../domain/quoteDisplay";
import { hasPromotionPricingIssue, promotionPricingMessage } from "../domain/promotionPricing";

export { getCartReasonLabel } from "../domain/cartChangeReasons";
export { getCartValidationNotice } from "../domain/cartIssues";

const groupLabels = {
  retained: { english: "Your items", arabic: "منتجاتك" },
  adjusted: { english: "Quantity adjusted", arabic: "تم تعديل الكمية" },
  removed: {
    english: "Removed from this order",
    arabic: "منتجات مستبعدة من الطلب",
  },
};

const finiteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const localizedName = (value = {}, arabic = false) =>
  (arabic &&
    (value.ar_name ||
      value.name_ar ||
      value.arabic_name ||
      value.ar_title)) ||
  value.name ||
  value.title ||
  value.value ||
  "";

export const getCartPromotionLabels = (cart = {}) => {
  const labels = [];
  const coupon = cart.promos;
  if (coupon)
    labels.push(
      coupon.code ||
        coupon.name ||
        coupon.promo?.code ||
        coupon.promo?.name ||
        "Promotion",
    );
  (cart.smart_promos || []).forEach((promotion) => {
    const label = promotion.code || promotion.name || promotion.custom_label;
    if (label && !labels.includes(label)) labels.push(label);
  });
  return labels;
};

export const getCartLineStateLabels = (line, group, language = "english") => {
  const arabic = language === "arabic";
  const preorderDate =
    line.preorderDate || line.preorder_date || line.publishedDate || "";
  const adjustedQuantity = Number(line.quantity);
  return [
    line.isPromotionLine
      ? arabic
        ? "هدية ترويجية"
        : "Promotion item"
      : null,
    line.isPreordered
      ? preorderDate
        ? arabic
          ? `طلب مسبق — الأقرب ${preorderDate}`
          : `Preorder — earliest ${preorderDate}`
        : arabic
          ? "طلب مسبق"
          : "Preorder"
      : null,
    group === "adjusted"
      ? Number.isFinite(adjustedQuantity) && adjustedQuantity > 0
        ? arabic
          ? `متوفر اليوم ${adjustedQuantity} فقط`
          : `Only ${adjustedQuantity} available today`
        : arabic
          ? "تم تعديل الكمية إلى الحد المتاح"
          : "Quantity adjusted to the available limit"
      : null,
  ].filter(Boolean);
};

const CART_ASAP_REFRESH_ERRORS = new Set([
  "asap_authority_unavailable",
  "asap_context_changed",
  "asap_window_mismatch",
  "asap_cart_changed",
]);

export const shouldRefreshCartAsapAuthority = (error) =>
  CART_ASAP_REFRESH_ERRORS.has(`${error || ""}`);

export { getCartLineDetails, getCartProductPresentation } from "../domain/cartPresentation";

export const getCartQuantityState = ({
  item = {},
  product = {},
  otherQuantity = 0,
  usedComponents = {},
} = {}) => {
  const quantity = Math.max(0, finiteNumber(item.quantity, 0));
  const increment = Math.max(
    0.001,
    finiteNumber(product.increments ?? item.increments, 1),
  );
  const minimum = Math.max(
    increment,
    finiteNumber(
      product.min_addable_quantity ?? item.min_addable_quantity,
      increment,
    ),
  );
  const rawMaximum =
    product.max_addable_quantity ?? item.max_addable_quantity ?? null;
  const maximum =
    rawMaximum === null || rawMaximum === undefined
      ? null
      : finiteNumber(rawMaximum, 0);
  const hideQuantity =
    product.hide_quantity_box === true || item.hide_quantity_box === true;
  let inventoryAllowsIncrease = true;
  if (product.type_of_product === "stocked") {
    inventoryAllowsIncrease =
      finiteNumber(getProductInventory(product, usedComponents), 0) >= increment;
  }
  let modifierMaximum = Number.POSITIVE_INFINITY;
  try {
    modifierMaximum = getMaximumProductInventoryFromModifiers(
      item.options || {},
      quantity,
      usedComponents,
      null,
      null,
      product,
    );
  } catch (error) {}
  const nextQuantity = Number((quantity + increment).toFixed(6));
  const previousQuantity = Number((quantity - increment).toFixed(6));
  return {
    quantity,
    increment,
    nextQuantity,
    previousQuantity,
    canDecrease: !hideQuantity && previousQuantity >= minimum,
    canIncrease:
      !hideQuantity &&
      product.available !== false &&
      inventoryAllowsIncrease &&
      nextQuantity <= modifierMaximum &&
      (maximum === null || nextQuantity + otherQuantity <= maximum),
  };
};

export const getMovMeterState = (validation = {}) => {
  const minimum = moneyToMajor(validation.minimumOrderValue);
  const remaining = moneyToMajor(validation.minimumOrderRemaining);
  if (minimum === null || minimum === undefined || minimum <= 0) return null;
  const safeRemaining = Math.max(0, finiteNumber(remaining, minimum));
  const current = Math.max(0, minimum - safeRemaining);
  return {
    minimum,
    remaining: safeRemaining,
    remainingValue: validation.minimumOrderRemaining,
    reached: safeRemaining <= 0,
    progress: Math.max(0, Math.min(100, (current / minimum) * 100)),
  };
};

export const getCartCheckoutState = ({
  activeLineCount = 0,
  hasError = false,
  mov = null,
  validating = false,
  validation = {},
} = {}) => {
  const movUnmet = Boolean(mov && !mov.reached);
  return {
    disabled:
      activeLineCount <= 0 ||
      hasError ||
      validating ||
      movUnmet ||
      validation.valid !== true,
    movUnmet,
  };
};

export const getCartContextParts = (
  context = {},
  detail = "",
  language = "english",
  presentation = {},
) => {
  const arabic = language === "arabic";
  if (!context.type)
    return { location: arabic ? "اختر طريقة ومكان الطلب" : "Choose order options", time: "" };
  const method =
    context.type === "pickup"
      ? arabic
        ? "استلام"
        : "Pickup"
      : arabic
        ? "توصيل"
        : "Delivery";
  const time =
    ["asap", "scheduled"].includes(context.time?.type)
        ? formatCheckoutTimeSummary(context.time, arabic, undefined, { ...presentation, type: context.type })
        : "";
  return { location: [method, detail].filter(Boolean).join(" · "), time };
};

export const getCartContextLabel = (...args) => {
  const { location, time } = getCartContextParts(...args);
  return [location, time].filter(Boolean).join(" · ");
};

export const getCartPriceRows = ({
  quote,
  validation = {},
  cart = {},
  context = {},
  language = "english",
} = {}) => {
  const arabic = language === "arabic";
  const pending = quote?.pricingStatus === "pending" || validation.pricingStatus === "pending";
  const subtotal = quote?.subtotal ?? validation.subtotal ?? cart.price;
  const delivery = pending ? null : quote?.deliveryRate ?? validation.deliveryRate;
  const rows = [{
    key: "subtotal",
    label: arabic ? "المجموع الفرعي" : "Subtotal",
    value: subtotal,
  }];
  if (context.type === "delivery") {
    rows.push({
      key: "delivery",
      label: arabic ? "التوصيل" : "Delivery",
      value: delivery,
      pending: moneyToMajor(delivery) === null,
    });
  }
  const optionalRows = [
    ["discountTotal", arabic ? "الخصم" : "Discount", true],
    ["additionalFee", arabic ? "رسوم إضافية" : "Additional fee"],
    ["giftWrapping", arabic ? "تغليف الهدية" : "Gift wrapping"],
  ];
  optionalRows.forEach(([key, label, deduction]) => {
    if ((moneyToMajor(quote?.[key]) ?? 0) > 0)
      rows.push({ key, label, value: quote[key], deduction });
  });
  if ((moneyToMajor(quote?.vatAmount) ?? 0) > 0) {
    // VAT may already be included in item prices. Keep the server total intact
    // and label included tax rather than implying it is charged a second time.
    const beforeVat = Math.max(0,
      (moneyToMajor(subtotal) ?? 0) +
      (moneyToMajor(quote.deliveryRate) ?? 0) +
      (moneyToMajor(quote.additionalFee) ?? 0) +
      (moneyToMajor(quote.giftWrapping) ?? 0) -
      (moneyToMajor(quote.discountTotal) ?? 0),
    );
    const total = moneyToMajor(quote.orderTotal);
    const included = total !== null && Math.abs(total - beforeVat) < 0.000001;
    rows.push({
      key: "vat",
      label: included
        ? arabic ? "الضريبة (مشمولة)" : "VAT (included)"
        : arabic ? "الضريبة" : "VAT",
      value: quote.vatAmount,
    });
  }
  const rounding = getQuoteDisplayRounding(quote, quote?.displayCurrency || validation.displayCurrency);
  if (rounding) rows.push({ key: "currency-rounding", label: arabic ? "فرق تقريب العملة" : "Currency rounding", value: rounding });
  return rows;
};

export const getCartDisplayedTotal = ({ quote, validation, cart } = {}) => {
  if (
    quote?.pricingStatus === "pending" ||
    validation?.pricingStatus === "pending"
  )
    return null;
  if (quote?.orderTotal !== null && quote?.orderTotal !== undefined)
    return quote.orderTotal;
  const subtotalMoney = normalizeMoneyValue(validation?.subtotal);
  const deliveryMoney = normalizeMoneyValue(validation?.deliveryRate);
  if (
    subtotalMoney &&
    typeof subtotalMoney === "object" &&
    (!deliveryMoney ||
      (typeof deliveryMoney === "object" &&
        deliveryMoney.currency === subtotalMoney.currency &&
        deliveryMoney.fractionDigits === subtotalMoney.fractionDigits))
  )
    return {
      ...subtotalMoney,
      amountMinor:
        subtotalMoney.amountMinor +
        (typeof deliveryMoney === "object" ? deliveryMoney.amountMinor : 0),
    };
  const subtotal =
    moneyToMajor(validation?.subtotal) ?? finiteNumber(cart?.price, 0);
  const delivery = moneyToMajor(validation?.deliveryRate) ?? 0;
  return Math.max(0, subtotal + delivery);
};

export const buildCartValidationRequest = (props, restoreLineId = null) => {
  const contextFingerprint = createFulfillmentFingerprint(props.context);
  return {
    contextFingerprint,
    request: {
      context: {
        ...props.context,
        fingerprint: contextFingerprint,
      },
      lines: props.lines,
      draft: props.draft,
      promotions: {
        promo: props.cart.promos,
        discounts: props.cart.discounts,
        smartPromos: props.cart.smart_promos,
      },
      currencyCode: props.currencyCode || null,
      ...(restoreLineId ? { restoreLineId } : {}),
    },
  };
};

export const cartValidationRequestKey = (props) =>
  JSON.stringify(buildCartValidationRequest(props).request);

export class CartDrawerV2 extends Component {
  static defaultProps = {
    api: checkoutV2Api,
    cart: {},
    classification: { retained: [], adjusted: [], removed: [] },
    context: {},
    draft: {},
    lines: [],
    rawLineMeta: {},
    rawRegularItems: [],
    settings: {},
    validation: {},
    telemetry: recordStorefrontV2Telemetry,
    clearQuote: () => {},
    onOpen: () => {},
    setQuote: () => {},
  };
  state = {
    closing: false,
    error: "",
    lastRemoved: null,
    removedNoticeCount: 0,
    restoring: null,
    updatingLineId: null,
    validating: false,
  };
  mounted = false;
  validationRequestId = 0;
  restoreRequestId = 0;
  asapAuthorityRequestKey = "";
  asapValidationRetryKey = "";

  componentDidMount() {
    this.mounted = true;
    if (this.props.history?.listen && this.props.history?.location) {
      this.drawerHistory = new CartDrawerHistory(
        this.props.history,
        () => this.requestClose(),
        () => this.props.onOpen(),
        this.props.historyScope,
      );
      if (this.props.open) this.drawerHistory.open();
      else if (this.drawerHistory.active) this.props.onOpen();
    }
    if (this.props.open && !this.ensureAsapAuthority()) this.refreshValidation();
  }

  componentDidUpdate(prevProps, prevState = {}) {
    const previousRemoved = visibleCartChanges(prevProps.classification || {}, prevProps.dismissedCartChanges, prevProps.context).removed.length;
    const currentRemoved = visibleCartChanges(this.props.classification || {}, this.props.dismissedCartChanges, this.props.context).removed.length;
    if (currentRemoved > previousRemoved)
      this.setState({ removedNoticeCount: currentRemoved });
    const opened = !prevProps.open && this.props.open;
    const newAdjustment = (this.props.classification?.adjusted?.length || 0) > (prevProps.classification?.adjusted?.length || 0);
    if (this.scrollNode && (opened || currentRemoved > previousRemoved || newAdjustment ||
      (this.state.error && this.state.error !== prevState.error))) this.scrollNode.scrollTop = 0;
    if (prevProps.validation !== this.props.validation && this.props.validation?.valid === true &&
      !this.state.validating && this.state.error) this.setState({ error: "" });
    if (prevProps.open && !this.props.open && this.state.lastRemoved)
      this.setState({ lastRemoved: null });
    if (opened && this.drawerHistory) this.drawerHistory.open();
    const inputsChanged =
      this.props.open &&
      cartValidationRequestKey(prevProps) !==
        cartValidationRequestKey(this.props);
    if ((opened || inputsChanged) && !this.ensureAsapAuthority())
      this.refreshValidation();
  }

  ensureAsapAuthority = () => {
    const { context, deliveryTimeLoading } = this.props;
    if (getFulfillmentIssues(context).length) return false;
    const missingAuthority =
      !!context.type &&
      context.time?.type === "asap" &&
      !context.time?.authority;
    if (!missingAuthority) {
      this.asapAuthorityRequestKey = "";
      return false;
    }
    const requestKey = JSON.stringify([
      context.type,
      context.countryId,
      context.areaId,
      context.branchId,
      context.branchDeliveryChargeId,
    ]);
    if (!deliveryTimeLoading && this.asapAuthorityRequestKey !== requestKey) {
      this.asapAuthorityRequestKey = requestKey;
      // The connected context update starts validation with the fresh proof.
      // If renewal fails, do not leave a disabled checkout button indefinitely.
      Promise.resolve()
        .then(() => this.props.fetchDeliveryTime())
        .then(() => {
          if (
            this.mounted && this.props.open &&
            this.asapAuthorityRequestKey === requestKey &&
            this.props.context.time?.type === "asap" &&
            !this.props.context.time.authority
          ) this.setState({ validating: false, error: "validation_unavailable" });
        })
        .catch(() => {
          if (
            this.mounted && this.props.open &&
            this.asapAuthorityRequestKey === requestKey
          ) this.setState({ validating: false, error: "validation_unavailable" });
        });
    }
    if (!this.state.validating || this.state.error)
      this.setState({ validating: true, error: "" });
    return true;
  };

  refreshAsapAndValidation = async (requestId, requestKey) => {
    const refreshKey = JSON.stringify([
      createCartFingerprint(this.props.lines),
      this.props.context.type,
      this.props.context.countryId,
      this.props.context.areaId,
      this.props.context.branchId,
      this.props.context.branchDeliveryChargeId,
    ]);
    if (this.asapValidationRetryKey === refreshKey) return false;
    this.asapValidationRetryKey = refreshKey;
    const refreshedDeliveryTime = await this.props.fetchDeliveryTime();
    if (!this.mounted || requestId !== this.validationRequestId)
      return true;
    const authority = `${refreshedDeliveryTime?.authority || ""}`.trim();
    if (!authority || authority === this.props.context.time?.authority) {
      this.setState({ validating: false, error: "asap_authority_unavailable" });
      return false;
    }
    // FETCH_DELIVERY_TIME commits the new token. The connected context change
    // is the single owner of the next validation request, preventing parallel
    // old/new-authority responses from racing in the drawer.
    return true;
  };

  componentWillUnmount() {
    this.mounted = false;
    if (this.drawerHistory) this.drawerHistory.dispose();
    this.validationRequestId += 1;
    this.restoreRequestId += 1;
  }

  requestClose = (afterClose = this.props.onClose) => {
    if (this.state.closing) return;
    this.afterClose = afterClose;
    this.setState({ closing: true });
  };

  finishClose = () => {
    if (!this.state.closing) return;
    const finish = () => {
      if (!this.mounted) return;
      const afterClose = this.afterClose;
      this.afterClose = null;
      if (typeof afterClose === "function") afterClose();
      if (this.mounted) this.setState({ closing: false });
    };
    if (this.drawerHistory) this.drawerHistory.close(finish);
    else finish();
  };

  close = () => this.requestClose();
  changeContext = () => this.requestClose(this.props.onChangeContext);
  continueShopping = () => this.requestClose(this.props.onContinueShopping || this.props.onClose);
  checkout = () => this.requestClose(this.props.onCheckout || this.props.onClose);

  retryValidation = () => {
    this.asapAuthorityRequestKey = "";
    this.asapValidationRetryKey = "";
    if (!this.ensureAsapAuthority()) this.refreshValidation();
  };

  refreshValidation = async () => {
    const requestId = ++this.validationRequestId;
    if (getFulfillmentIssues(this.props.context).length) {
      // No location is not an unavailable location. Never classify/remove
      // items against a null branch on a first visit.
      this.setState({ validating: false, error: "" });
      this.props.setValidation({ valid: false, retainedLines: [], adjustedLines: [], removedLines: [], errors: [] });
      if (this.props.clearQuote) this.props.clearQuote();
      return;
    }
    if (!this.props.lines.length) {
      this.setState({ validating: false, error: "" });
      this.props.setValidation({
        valid: false,
        retainedLines: [],
        adjustedLines: [],
        removedLines: [],
        errors: [],
        checkedAt: Date.now(),
      });
      return;
    }
    const requestKey = cartValidationRequestKey(this.props);
    const { contextFingerprint, request } = buildCartValidationRequest(
      this.props,
    );
    const cartFingerprint = createCartFingerprint(this.props.lines);
    this.setState({ validating: true, error: "" });
    if (this.props.clearQuote) this.props.clearQuote();
    try {
      const validation = await this.props.api.validateCartContext(request);
      if (
        !this.mounted ||
        requestId !== this.validationRequestId ||
        requestKey !== cartValidationRequestKey(this.props)
      )
        return;
      if (!validation.success) throw new Error("validation_rejected");
      const validationError = (validation.errors || [])[0] || "";
      if (
        this.props.context.time?.type === "asap" &&
        shouldRefreshCartAsapAuthority(validationError)
      ) {
        if (await this.refreshAsapAndValidation(requestId, requestKey)) return;
      }
      this.asapValidationRetryKey = "";
      this.props.setValidation({ ...validation, contextFingerprint, cartFingerprint });
      if (
        validation.valid &&
        getEffectiveCheckoutLines(this.props.lines, validation).length > 0 &&
        typeof this.props.api.fetchCheckoutQuote === "function"
      ) {
        const quote = await this.props.api.fetchCheckoutQuote({
          ...request,
          lines: getEffectiveCheckoutLines(this.props.lines, validation),
        });
        if (
          !this.mounted ||
          requestId !== this.validationRequestId ||
          requestKey !== cartValidationRequestKey(this.props)
        )
          return;
        if (!quote?.success) throw new Error("quote_rejected");
        if (this.props.setQuote)
          this.props.setQuote({ ...quote, contextFingerprint,
            clientCartFingerprint: createCartFingerprint(getEffectiveCheckoutLines(request.lines, validation)),
          });
      }
      this.setState({
        validating: false,
        error: validation.valid
          ? ""
          : validation.errors[0] || "context_invalid",
      });
    } catch (error) {
      if (
        !this.mounted ||
        requestId !== this.validationRequestId ||
        requestKey !== cartValidationRequestKey(this.props)
      )
        return;
      this.setState({ validating: false, error: "validation_unavailable" });
    }
  };

  restore = async (line) => {
    if (getFulfillmentIssues(this.props.context).length) {
      this.changeContext();
      return;
    }
    if (this.state.restoring) return;
    const requestId = ++this.restoreRequestId;
    const requestKey = cartValidationRequestKey(this.props);
    const { contextFingerprint, request } = buildCartValidationRequest(
      this.props,
      line.lineId,
    );
    this.setState({ restoring: line.lineId, error: "" });
    try {
      const validation = await this.props.api.validateCartContext(request);
      if (
        !this.mounted ||
        requestId !== this.restoreRequestId ||
        requestKey !== cartValidationRequestKey(this.props)
      ) {
        if (this.mounted && requestId === this.restoreRequestId)
          this.setState({ restoring: null });
        return;
      }
      if (!validation.success) throw new Error("restore_rejected");
      this.props.setValidation({ ...validation, contextFingerprint });
      const remainsRemoved = validation.removedLineIds.includes(line.lineId);
      if (!remainsRemoved)
        (this.props.telemetry || recordStorefrontV2Telemetry)(
          STOREFRONT_V2_TELEMETRY.CART_LINE_RESTORED,
          "cart_restore",
        );
      this.setState({
        restoring: null,
        error: remainsRemoved ? "still_unavailable" : "",
      });
    } catch (error) {
      if (!this.mounted || requestId !== this.restoreRequestId) return;
      this.setState({ restoring: null, error: "restore_unavailable" });
    }
  };

  getLineMeta = (line) => this.props.rawLineMeta[`${line.lineId}`] || {
    displayName: (this.props.language === "arabic" && line.arName) || line.name || `#${line.productId || ""}`,
  };

  getQuantityState = (line) => {
    const meta = this.getLineMeta(line);
    const otherQuantity = this.props.rawRegularItems.reduce(
      (total, item, index) =>
        index !== meta.index && `${item.id}` === `${meta.item?.id}`
          ? total + finiteNumber(item.quantity, 0)
          : total,
      0,
    );
    return getCartQuantityState({
      item: {
        ...(meta.item || {}),
        quantity: line.quantity ?? meta.item?.quantity,
      },
      product: meta.product,
      otherQuantity,
      usedComponents: this.props.usedComponents,
    });
  };

  changeQuantity = async (line, direction) => {
    const meta = this.getLineMeta(line);
    if (!meta.item || meta.isPromotionLine || this.state.updatingLineId) return;
    const quantity = this.getQuantityState(line);
    if (
      (direction > 0 && !quantity.canIncrease) ||
      (direction < 0 && !quantity.canDecrease)
    )
      return;
    const next = direction > 0 ? quantity.nextQuantity : quantity.previousQuantity;
    const change = direction > 0 ? quantity.increment : -quantity.increment;
    this.setState({ updatingLineId: line.lineId, lastRemoved: null });
    try {
      await this.props.editQuantity(
        meta.index,
        meta.item,
        next,
        change,
        false,
        meta.item.is_preordered,
        meta.product?.max_addable_quantity ?? null,
      );
    } finally {
      if (this.mounted) this.setState({ updatingLineId: null });
    }
  };

  removeLine = async (line, group = "retained") => {
    const meta = this.getLineMeta(line);
    if (!meta.item || this.state.updatingLineId) return;
    // Capture before dispatch: removal updates the classification too. Undo
    // is useful for a voluntary removal, not for an already rejected item.
    const canUndo = group !== "removed" && !this.props.classification?.removed?.some(
      removed => `${removed.lineId}` === `${line.lineId}`,
    );
    this.setState({ updatingLineId: line.lineId, lastRemoved: null });
    try {
      await this.props.removeLine(meta.index, meta.item, meta.isPromotionLine === true);
      if (canUndo) trackCartRemoval(this.props.settings, meta.item);
      if (this.mounted)
        this.setState({
          lastRemoved: this.props.open && canUndo ? { item: meta.item, name: meta.displayName, promotion: meta.isPromotionLine === true } : null,
          updatingLineId: null,
        });
    } catch (error) {
      if (this.mounted)
        this.setState({ updatingLineId: null, error: "remove_unavailable" });
    }
  };

  undoRemove = async () => {
    const removed = this.state.lastRemoved;
    if (!removed) return;
    this.setState({ lastRemoved: null });
    await this.props.restoreRemovedLine(removed.item, removed.promotion === true);
  };

  editLine = (line) => {
    const meta = this.getLineMeta(line);
    const product = meta.product || {};
    if (!meta.item || !product.slug) return;
    this.props.setCurrentProduct(null);
    this.requestClose(() => {
      if (this.props.onClose) this.props.onClose();
      this.props.history.push({
        pathname: `/product/${product.category}/${
          product.parent ? `${product.parent}/` : ""
        }${product.slug}`,
        state: {
          editMode: true,
          editedProduct: { ...meta.item, index: meta.index },
        },
      });
    });
  };

  getLinePrice = (line) => {
    if (
      line.priceQuote?.lineTotal !== null &&
      line.priceQuote?.lineTotal !== undefined
    )
      return line.priceQuote.lineTotal;
    const meta = this.getLineMeta(line);
    const item = meta.item || {};
    return (
      finiteNumber(item.total_price ?? item.price, 0) *
        finiteNumber(item.quantity, 1) +
      finiteNumber(item.quantityExcludedOptionsTotal, 0)
    );
  };

  getDisplayCurrency = () =>
    this.props.quote?.displayCurrency ||
    this.props.validation?.displayCurrency ||
    null;

  renderLine(line, group, arabic, index) {
    const meta = this.getLineMeta(line);
    const item = meta.item || {};
    const product = meta.product || item;
    const presentation = getCartProductPresentation(meta, this.props.language);
    const displayName =
      presentation.name ||
      line.name ||
      `${arabic ? "منتج" : "Item"} ${line.productId || ""}`;
    const details = getCartLineDetails(item, product, this.props.language);
    const expandableDetails = details.length > 2 || details.join(" · ").length > 100;
    const stateLabels = getCartLineStateLabels(
      {
        ...line,
        publishedDate: item.publishedDate || product.published_date,
      },
      group,
      this.props.language,
    );
    const quantity = this.getQuantityState(line);
    const updating = this.state.updatingLineId === line.lineId;
    const image =
      product.photo_thumb || product.photo_small || product.photo || item.photo;
    const editable = !meta.isPromotionLine && !!product.slug && (group !== "removed" || [
      "required_extra_field_missing", "extra_field_unavailable", "option_unavailable",
      "option_quantity_invalid", "variant_required",
    ].includes(line.reason));
    return (
      <article
        className={`checkout-v2-cart-line checkout-v2-cart-line--${group}`}
        key={line.lineId || `${group}-${index}`}
      >
        <div className="checkout-v2-cart-line__image">
          {image ? (
            <img src={image} alt="" />
          ) : (
            <ShoppingBasketOutlinedIcon aria-hidden="true" />
          )}
        </div>
        <div className="checkout-v2-cart-line__content">
          <div className="checkout-v2-cart-line__heading">
            <div>
              <h4>{displayName}</h4>
              {!!presentation.variants && (
                <p className="checkout-v2-cart-line__variants">{presentation.variants}</p>
              )}
              {!!details.length && !expandableDetails && (
                <p className="checkout-v2-cart-line__details">
                  {details.join(" · ")}
                </p>
              )}
              {expandableDetails && (
                <details className="checkout-v2-cart-line__customizations">
                  <summary>{arabic ? "عرض التفاصيل" : "View details"} <span>({details.length})</span></summary>
                  <ul>{details.map((detail, detailIndex) => <li key={detailIndex}>{detail}</li>)}</ul>
                </details>
              )}
            </div>
            <strong className="checkout-v2-cart-line__price">
              <Money
                currency={this.props.settings.currency_iso}
                decimals={this.props.settings.currency_decimals}
                displayCurrency={this.getDisplayCurrency()}
                language={this.props.language}
                value={this.getLinePrice(line)}
              />
            </strong>
          </div>
          {!!stateLabels.length && (
            <div className="checkout-v2-cart-line__badges">
              {stateLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          )}
          {!!line.reason && (
            <p className="checkout-v2-cart-line__reason">
              {getCartReasonLabel(line.reason, this.props.language)}
            </p>
          )}
        </div>
        <div className="checkout-v2-cart-line__actions">
            {group !== "removed" && !meta.isPromotionLine && (
              <div
                className="checkout-v2-cart-quantity"
                aria-label={arabic ? "الكمية" : "Quantity"}
              >
                <button
                  aria-label={arabic ? "تقليل الكمية" : "Decrease quantity"}
                  disabled={!quantity.canDecrease || updating}
                  onClick={() => this.changeQuantity(line, -1)}
                  type="button"
                >
                  <RemoveRoundedIcon />
                </button>
                <span aria-live="polite" aria-busy={updating}>{quantity.quantity}</span>
                <button
                  aria-label={arabic ? "زيادة الكمية" : "Increase quantity"}
                  disabled={!quantity.canIncrease || updating}
                  onClick={() => this.changeQuantity(line, 1)}
                  type="button"
                >
                  <AddRoundedIcon />
                </button>
              </div>
            )}
            {meta.isPromotionLine && group !== "removed" && (
              <span className="checkout-v2-cart-line__gift-quantity">
                {arabic ? "الكمية" : "Qty"}: {line.quantity || 1}
              </span>
            )}
            <div className="checkout-v2-cart-line__secondary-actions">
              {editable && (
                <button onClick={() => this.editLine(line)} type="button">
                  <EditOutlinedIcon />
                  {arabic ? "تعديل" : "Edit"}
                </button>
              )}
              {(!meta.isPromotionLine || group === "removed") && (
                <button
                  className="is-destructive"
                  disabled={updating}
                  onClick={() => group === "removed" && !meta.item
                    ? this.props.dismissCartChanges([cartChangeNoticeKey(line, group, this.props.context)])
                    : this.removeLine(line, group)}
                  type="button"
                >
                  <DeleteOutlineRoundedIcon />
                  {group === "removed" && !meta.item ? (arabic ? "إخفاء" : "Dismiss") : (arabic ? "إزالة" : "Remove")}
                </button>
              )}
            </div>
        </div>
      </article>
    );
  }

  renderGroup(key, lines, arabic) {
    if (!lines.length) return null;
    return (
      <section className={`checkout-v2-cart-group checkout-v2-cart-group--${key}`}>
        <div className="checkout-v2-cart-group__heading">
          <h3>{groupLabels[key][arabic ? "arabic" : "english"]}</h3>
          {key !== "retained" && <span>{lines.length}</span>}
          {key !== "retained" && <button
            className="checkout-v2-cart-group__dismiss"
            type="button"
            aria-label={arabic ? key === "removed" ? "إخفاء إشعار المنتجات المستبعدة" : "إخفاء إشعار تعديل الكمية" : key === "removed" ? "Dismiss removed items notice" : "Dismiss quantity changes"}
            onClick={() => this.props.dismissCartChanges(lines.map(line => cartChangeNoticeKey(line, key, this.props.context)))}
          ><CloseRoundedIcon /></button>}
        </div>
        {key === "removed" && (
          <div className="checkout-v2-cart-group__removed-help">
            <p>
              {arabic
                ? "تمت إزالة هذه المنتجات غير المتاحة من السلة والإجمالي. يمكنك تغيير خيارات الطلب ثم إضافتها مجدداً من القائمة عند توفرها."
                : "These unavailable items were removed from your cart and total. You can change order options and add them again from the menu if available."}
            </p>
            {this.props.onChangeContext && (
              <button onClick={this.changeContext} type="button">
                {arabic ? "تغيير خيارات الطلب" : "Change order options"}
              </button>
            )}
          </div>
        )}
        <div className="checkout-v2-cart-group__lines">
          {lines.map((line, index) =>
            this.renderLine(line, key, arabic, index),
          )}
        </div>
      </section>
    );
  }

  renderMov(mov, arabic) {
    if (
      this.props.settings.show_mov_meter === false ||
      !mov ||
      this.props.classification.retained.length +
        this.props.classification.adjusted.length <=
        0
    )
      return null;
    return (
      <section
        className={`checkout-v2-cart-mov ${mov.reached ? "is-reached" : ""}`}
      >
        <div className="checkout-v2-cart-mov__copy">
          <div>
            {mov.reached ? <CheckCircleRoundedIcon /> : null}
            <strong>
              {mov.reached
                ? arabic
                  ? "تم الوصول للحد الأدنى"
                  : "Minimum order met"
                : arabic
                  ? "اقتربت من إتمام الطلب"
                  : "You’re almost there"}
            </strong>
          </div>
          {!mov.reached && (
            <span>
              {arabic ? "أضف" : "Add"}{" "}
              <Money
                currency={this.props.settings.currency_iso}
                decimals={this.props.settings.currency_decimals}
                displayCurrency={this.getDisplayCurrency()}
                language={this.props.language}
                value={mov.remainingValue}
              />
            </span>
          )}
        </div>
        {!mov.reached && <div className="checkout-v2-cart-mov__track" role="progressbar" aria-label={arabic ? "الحد الأدنى للطلب" : "Minimum order"} aria-valuenow={Math.round(mov.progress)} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${mov.progress}%` }} />
        </div>}
      </section>
    );
  }

  renderValidationIssue(arabic, notice) {
    if (!this.state.error) return null;
    return <section className="checkout-v2-cart__error" data-cart-validation-error={this.state.error} role="alert">
      <strong>{notice.title}</strong>
      <p>{notice.message}</p>
      {notice.reference && <small>{arabic ? "المرجع" : "Reference"}: {notice.reference}</small>}
      <div>
        <StorefrontActionButton onClick={notice.contextAction ? this.changeContext : this.retryValidation} tone="secondary">
          {notice.actionLabel}
        </StorefrontActionButton>
        <StorefrontActionButton onClick={this.continueShopping} tone="quiet">
          {arabic ? "إضافة منتجات أخرى" : "Add other items"}
        </StorefrontActionButton>
      </div>
    </section>;
  }

  render() {
    const { open, language, classification, context } = this.props;
    const displayed = visibleCartChanges(classification, this.props.dismissedCartChanges, context);
    const onClose = this.close;
    const reducedMotion = typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const arabic = language === "arabic";
    const activeLineCount =
      classification.retained.length + classification.adjusted.length;
    const itemQuantity = [...classification.retained, ...classification.adjusted]
      .reduce((total, line) => total + finiteNumber(line.quantity, 0), 0);
    const promotionLabels = getCartPromotionLabels({
      ...this.props.cart,
      // The code field already confirms applied coupons; only list additional
      // automatic promotions here, unless the merchant hides that field.
      promos: this.props.settings.enable_coupons_store ? null : this.props.cart.promos,
    });
    const contextParts = getCartContextParts(
      context,
      this.props.contextDetail,
      language,
      { settings: this.props.settings, cart: this.props.cart,
        branch: this.props.branches?.find(branch => String(branch.id) === String(context.branchId)) },
    );
    const mov = getMovMeterState(this.props.validation);
    const priceRows = getCartPriceRows(this.props);
    const pricingPending =
      this.props.quote?.pricingStatus === "pending" ||
      this.props.validation?.pricingStatus === "pending";
    const totalPending = pricingPending || priceRows.some((row) => row.pending);
    const total = getCartDisplayedTotal({
      quote: this.props.quote,
      validation: this.props.validation,
      cart: this.props.cart,
    });
    const checkoutState = getCartCheckoutState({
      activeLineCount,
      hasError: Boolean(this.state.error),
      mov,
      validating: this.state.validating,
      validation: this.props.validation,
    });
    const needsContext = getFulfillmentIssues(context).length > 0;
    const validationNotice = getCartValidationNotice(
      this.state.error,
      language,
    );
    return (
      <>
        <Drawer
          anchor={arabic ? "left" : "right"}
          classes={{ paper: "checkout-v2-cart-drawer__paper" }}
          ModalProps={{ keepMounted: true }}
          onClose={onClose}
          open={open && !this.state.closing}
          transitionDuration={reducedMotion ? 0 : { enter: 240, exit: 240 }}
          SlideProps={{ onExited: this.finishClose }}
        >
          <div
            className="checkout-v2-cart storefront-v2-surface"
            dir={arabic ? "rtl" : "ltr"}
            role="dialog"
            aria-label={arabic ? "سلة المشتريات" : "Your cart"}
            style={{
              "--checkout-cart-accent":
                this.props.settings.dark ||
                this.props.settings.primary_color ||
                "#009dcc",
            }}
          >
          <header className="checkout-v2-cart__header">
            <div className="checkout-v2-cart__title">
              <span className="checkout-v2-cart__title-icon" aria-hidden="true"><ShoppingBasketOutlinedIcon /></span>
              <h2>{arabic ? "سلة المشتريات" : "Your cart"}</h2>
              <span className="checkout-v2-cart__count" aria-label={`${itemQuantity} ${arabic ? "منتج" : itemQuantity === 1 ? "item" : "items"}`}>
                {itemQuantity}
              </span>
            </div>
            <button
              aria-label={arabic ? "إغلاق السلة" : "Close cart"}
              className="checkout-v2-cart__close"
              onClick={onClose}
              type="button"
            >
              <CloseRoundedIcon />
            </button>
          </header>

          <div className="checkout-v2-cart__scroll" ref={node => { this.scrollNode = node; }}>
            <button
              className="checkout-v2-cart-context"
              onClick={this.changeContext}
              type="button"
            >
              <span className="checkout-v2-cart-context__icon">
                {context.type === "pickup" ? <StorefrontOutlinedIcon /> : <LocalShippingOutlinedIcon />}
              </span>
              <span>
                <strong>{contextParts.location}</strong>
                {!!contextParts.time && <small>{contextParts.time}</small>}
              </span>
              <em>{arabic ? "تغيير" : "Change"}</em>
            </button>

            {!(classification.removed.length > 0 && activeLineCount === 0) && this.renderValidationIssue(arabic, validationNotice)}
            {this.renderGroup("removed", displayed.removed, arabic)}
            {this.renderGroup("adjusted", displayed.adjusted, arabic)}
            {this.renderMov(mov, arabic)}

            {!displayed.retained.length && !displayed.adjusted.length && !displayed.removed.length ? (
              <section className="checkout-v2-cart-empty">
                <span><ShoppingBasketOutlinedIcon /></span>
                <h3>{arabic ? "سلتك فارغة" : "Your cart is empty"}</h3>
                <p>
                  {arabic
                    ? "أضف منتجاتك المفضلة، وستظهر هنا."
                    : "Add something you love and it’ll appear here."}
                </p>
                <StorefrontActionButton
                  onClick={this.continueShopping}
                >
                  {arabic ? "العودة إلى القائمة" : "Back to menu"}
                </StorefrontActionButton>
              </section>
            ) : (
              <>
                {this.renderGroup("retained", displayed.retained, arabic)}
              </>
            )}

            {activeLineCount > 0 && (
              <section className="checkout-v2-cart__pricing" aria-label={arabic ? "ملخص السعر" : "Price summary"}>
                {this.props.settings.enable_coupons_store && <CartPromotionCode />}
                {!!promotionLabels.length && (
                  <div aria-label={arabic ? "العروض المطبقة" : "Applied promotions"} className="checkout-v2-cart__promotion-pills">
                    <LocalOfferOutlinedIcon aria-hidden="true" />
                    <div>{promotionLabels.map((label) => <span key={label}>{label}</span>)}</div>
                  </div>
                )}
                <dl className="checkout-v2-cart__breakdown">
                  {priceRows.map((row) => <div key={row.key} className={row.deduction ? "is-discount" : ""}>
                    <dt>{row.label}</dt>
                    <dd>{row.pending ? arabic ? "يحدد لاحقاً" : "TBD" : <>
                      {row.deduction && <span aria-hidden="true">− </span>}
                      <Money currency={this.props.settings.currency_iso} decimals={this.props.settings.currency_decimals} displayCurrency={this.getDisplayCurrency()} language={language} value={row.value} />
                    </>}</dd>
                  </div>)}
                </dl>
                {pricingPending && <p className="checkout-v2-cart__pricing-note">{hasPromotionPricingIssue(this.props.quote?.pendingReasons)
                  ? promotionPricingMessage(arabic) : context.type === "delivery"
                  ? arabic ? "تُحدد رسوم التوصيل بعد إكمال عنوانك في صفحة الدفع." : "Delivery is calculated after you complete your address at checkout."
                  : arabic ? "يُؤكد المجموع النهائي في صفحة الدفع." : "The final total is confirmed at checkout."}</p>}
              </section>
            )}
            {activeLineCount === 0 &&
              this.props.lines.length > 0 &&
              this.props.onContinueShopping && (
                <StorefrontActionButton
                  className="checkout-v2-cart__all-removed-menu"
                  fullWidth
                  onClick={this.continueShopping}
                >
                  {arabic ? "إضافة منتجات من القائمة" : "Add items from menu"}
                </StorefrontActionButton>
              )}
          </div>

          {!!this.state.lastRemoved && (
            <div className="checkout-v2-cart__undo" role="status">
              <button
                className="checkout-v2-cart__undo-dismiss"
                aria-label={arabic ? "إخفاء إشعار الإزالة" : "Dismiss removal notice"}
                onClick={() => this.setState({ lastRemoved: null })}
                type="button"
              >
                <CloseRoundedIcon aria-hidden="true" />
              </button>
              <span>
                {arabic ? "تمت إزالة" : "Removed"}{" "}
                <strong>{this.state.lastRemoved.name}</strong>
              </span>
              <button onClick={this.undoRemove} type="button">
                {arabic ? "تراجع" : "Undo"}
              </button>
          </div>
          )}

          {activeLineCount > 0 && (
            <footer className="checkout-v2-cart__footer">
              <div className="checkout-v2-cart__footer-summary">
              {this.props.onContinueShopping && (
                <button
                  className="checkout-v2-cart__continue-shopping"
                  onClick={this.continueShopping}
                  type="button"
                >
                  {arabic ? <ArrowForwardRoundedIcon /> : <ArrowBackRoundedIcon />}
                  {arabic ? "إضافة منتجات أخرى" : "Add more items"}
                </button>
              )}
              <div className="checkout-v2-cart__subtotal">
                <span>{totalPending ? arabic ? "المجموع النهائي" : "Final total" : arabic ? "الإجمالي" : "Total"}</span>
                <strong>
                  {totalPending ? (
                    arabic ? "يحدد لاحقاً" : "TBD"
                  ) : (
                    <Money
                      currency={this.props.settings.currency_iso}
                      decimals={this.props.settings.currency_decimals}
                      displayCurrency={this.getDisplayCurrency()}
                      language={language}
                      value={total}
                    />
                  )}
                </strong>
              </div>
              </div>
              <StorefrontActionButton
                disabled={needsContext || (checkoutState.movUnmet && this.props.onContinueShopping) ? false : checkoutState.disabled}
                aria-busy={this.state.validating}
                fullWidth
                onClick={needsContext ? this.changeContext : checkoutState.movUnmet && this.props.onContinueShopping ? this.continueShopping : this.checkout}
              >
                <DeferredFeedback
                  active={!needsContext && this.state.validating && !checkoutState.movUnmet}
                  fallback={needsContext
                    ? arabic ? "اختيار خيارات الطلب" : "Choose order options"
                    : checkoutState.movUnmet
                    ? <>{arabic ? "أضف بقيمة" : "Add"}{" "}<Money currency={this.props.settings.currency_iso} decimals={this.props.settings.currency_decimals} displayCurrency={this.getDisplayCurrency()} language={language} value={mov.remainingValue} />{!arabic && " more"}</>
                    : this.props.onCheckout
                      ? arabic
                        ? "متابعة الدفع"
                        : "Go to checkout"
                      : arabic
                        ? "تمت المراجعة"
                        : "Done reviewing cart"
                  }
                >
                  {arabic ? "جاري تجهيز طلبك…" : "Getting your order ready…"}
                </DeferredFeedback>
                {arabic ? <ArrowBackRoundedIcon /> : <ArrowForwardRoundedIcon />}
              </StorefrontActionButton>
            </footer>
          )}
          </div>
        </Drawer>
        <Snackbar
          action={
            <>
            <button
              className="checkout-v2-cart__removed-notice-action"
              onClick={() => {
                this.setState({ removedNoticeCount: 0 });
                this.props.onOpen();
              }}
              type="button"
            >
              {arabic ? "عرض السلة" : "View cart"}
            </button>
            <button className="checkout-v2-cart__removed-notice-action" type="button"
              aria-label={arabic ? "إخفاء الإشعار" : "Dismiss cart change notification"}
              onClick={() => this.setState({ removedNoticeCount: 0 })}><CloseRoundedIcon /></button>
            </>
          }
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          autoHideDuration={5500}
          className="checkout-v2-cart__removed-notice"
          message={
            arabic
              ? `تمت إزالة ${this.state.removedNoticeCount} من المنتجات غير المتاحة`
              : `${this.state.removedNoticeCount} unavailable ${
                  this.state.removedNoticeCount === 1 ? "item was" : "items were"
                } moved out of your cart`
          }
          onClose={() => this.setState({ removedNoticeCount: 0 })}
          open={this.state.removedNoticeCount > 0}
        />
      </>
    );
  }
}

const buildRawLineMeta = (cart = {}, categories = [], language = "english") => {
  const arabic = language === "arabic";
  const regular = cart.cart || [];
  const promotion = cart.promo_items || [];
  const result = {};
  [...regular, ...promotion].forEach((item, absoluteIndex) => {
    const isPromotionLine = absoluteIndex >= regular.length;
    const index = isPromotionLine ? absoluteIndex - regular.length : absoluteIndex;
    const lineId = getCheckoutLineId(item, absoluteIndex);
    const product =
      getProduct(categories, item.id, item.category_id, item.parent) || item;
    result[lineId] = {
      displayName: localizedName(product, arabic) || item.name,
      parentProduct: item.parent ? getProduct(categories, item.parent, item.category_id) : null,
      index,
      isPromotionLine,
      item,
      product,
    };
  });
  return result;
};

export const getContextDetail = (store = {}, context = {}, language = "english") => {
  const arabic = language === "arabic";
  const branches = store.branches || [];
  const branch = branches.find((item) => `${item.id}` === `${context.branchId}`);
  return context.type === "pickup"
    ? localizedName(branch, arabic)
    : resolveAreaName(store, { area: context.areaId }, context.branchDeliveryChargeId, language);
};

const mapStateToProps = (state) => {
  const store = state.store?.store || {};
  const settings = store.settings || {};
  const cart = state.cart || {};
  const lines = buildCheckoutLines(cart);
  const validation = state.checkoutV2?.cartValidation || {};
  const context = normalizeFulfillmentContext({
    address: state.customers?.address,
    branchState: state.branch,
    branch: state.branch,
    deliveryTime: state.deliveryTime,
    settings,
  });
  return {
    abandonedCartID: state.abandonedCart?.id,
    language: state.store?.language || "english",
    settings,
    currencyCode: state.currency?.selected || null,
    cart,
    lines,
    validation,
    dismissedCartChanges: state.checkoutV2?.dismissedCartChanges || [],
    quote: state.checkoutV2?.quote || null,
    classification: classifyCheckoutLines(lines, validation),
    draft: state.checkoutV2?.draft || {},
    context,
    contextDetail: getContextDetail(store, context, state.store?.language),
    branches: store.branches || [],
    rawLineMeta: buildRawLineMeta(
      cart,
      store.categories || [],
      state.store?.language,
    ),
    rawRegularItems: cart.cart || [],
    usedComponents: state.store?.used_components || {},
    deliveryTimeLoading: state.deliveryTime?.loading === true,
  };
};

export default withRouter(
  connect(mapStateToProps, (dispatch) => ({
    dismissCartChanges: (keys) => dispatch(dismissCartChanges(keys)),
    editQuantity: (...args) => dispatch(edit_quantity(...args)),
    removeLine: (...args) => dispatch(remove_from_cart(...args)),
    restoreRemovedLine: (item, promotion) =>
      dispatch(promotion ? add_promo_item(item) : add_to_cart({ ...item })),
    fetchDeliveryTime: () => dispatch(fetchDeliveryTime()),
    setCurrentProduct: (product) => dispatch(setProduct(product)),
    setValidation: (validation) =>
      dispatch(setCheckoutV2CartValidation(validation)),
    setQuote: (quote) => dispatch(setCheckoutV2Quote(quote)),
    clearQuote: () => dispatch(clearCheckoutV2Quote()),
  }))(CartDrawerV2),
);
